import { Component, EventEmitter, OnInit, Output, inject } from '@angular/core';
import {
  MatCalendarCellClassFunction,
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TranslatePipe } from '@next/core-lib/i18n';
import { SiteState } from '@next/core-lib/site';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';

import { Production } from '../../models/production.model';
import * as ProductionActions from '../../store/actions/production.actions';
import {
  getProductionList,
  getSelectedProduction,
} from '../../store/selectors/production.selectors';

@Component({
  selector: 'app-production-picker',
  templateUrl: './production-picker.component.html',
  imports: [
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    TranslatePipe,
  ],
})
export class ProductionPickerComponent implements OnInit {
  private readonly store = inject<Store<SiteState>>(Store);

  @Output()
  productionChanged = new EventEmitter<Production>();

  public selectedDate: Date;
  private productionList: Production[];
  private productionList$ = this.store.select(getProductionList);
  private productionSelected$ = this.store.select(getSelectedProduction);

  public dateFilter = (date: Date): boolean => this.isInProductionList(date);

  ngOnInit(): void {
    this.store.dispatch(ProductionActions.loadProductionListRequest());
    this.productionList$
      .pipe(
        filter((productionList) => !!productionList),
        take(1),
      )
      .subscribe((productionList) => {
        this.productionList = productionList;
        this.productionSelected$.pipe(take(1)).subscribe((selectedProduction) => {
          if (selectedProduction !== null) {
            this.setSelectedDate(new Date(selectedProduction.date));
          } else {
            const latest = this.productionList.map((d) => new Date(d.date)).shift();
            if (latest !== undefined) {
              this.setSelectedDate(latest);
            }
          }
        });
      });
  }

  public datepickerDateClass: MatCalendarCellClassFunction<Date> = (cellDate, view) => {
    if (view === 'month') {
      return this.isInProductionList(cellDate) ? 'datepicker-custom-date-class' : '';
    }

    return '';
  };

  public onDateChanged(value: Date) {
    this.setSelectedDate(value);
  }

  private getDate(date: Date): Date {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0));
  }

  private isInProductionList(date: Date): boolean {
    if (date === null || date === undefined) {
      return false;
    }
    return (
      this.productionList.find(
        (d) => new Date(d.date).toISOString() === this.getDate(date).toISOString(),
      ) !== undefined
    );
  }

  private setSelectedDate(date: Date) {
    this.selectedDate = date;
    const selectedProduction = this.productionList.find(
      (production) =>
        this.getDate(new Date(production.date)).getTime() === this.getDate(date).getTime(),
    );
    this.productionChanged.emit(selectedProduction);
    this.store.dispatch(
      ProductionActions.setSelectedProduction({ production: selectedProduction }),
    );
  }
}
